import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import styles from './JobDetailNew.module.css'; 
import api from '../../api/api.js';
import showToast from "../../components/ShowToast";
import Loader2 from '../../components/loader2';
import LocationInputGroup from '../../components/Form/LocationInputGroup';
import { FileText, Camera, DollarSign, Megaphone, UserPlus, ArrowLeft, Check, Sparkles, Search as SearchIcon, X } from 'lucide-react';
import JobFileUploader from '../../components/Form/JobFileUploader';
import JobDescriptionEnhancer from '../../components/Form/JobDescriptionEnhancer';
import ProfessionSelector from '../../components/Form/ProfessionSelector';
import ModernNavbarPureCSS from '../../components/Navbar/ModernNavBarPureCSS';
import { jwtDecode } from 'jwt-decode';

// --- INTERNAL COMPONENT: Wizard Progress Bar ---
const WizardProgressBar = ({ currentStep }) => {
    const steps = [
        { id: 1, label: 'Service' },
        { id: 2, label: 'Details' },
        { id: 3, label: 'Logistics' },
        { id: 4, label: 'Review' }
    ];

    const progressPercentage = Math.min(100, Math.max(0, ((currentStep - 1) / (steps.length - 1)) * 100));

    return (
        <div style={{ maxWidth: '800px', margin: '0 auto 2rem auto', padding: '0 1rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', position: 'relative', marginBottom: '10px' }}>
                <div style={{ position: 'absolute', top: '50%', left: '0', right: '0', height: '2px', backgroundColor: '#e5e7eb', zIndex: 0, transform: 'translateY(-50%)' }}></div>
                <div style={{ position: 'absolute', top: '50%', left: '0', height: '2px', width: `${progressPercentage}%`, backgroundColor: '#0066ff', zIndex: 0, transform: 'translateY(-50%)', transition: 'width 0.3s ease' }}></div>

                {steps.map((step) => {
                    const isCompleted = step.id < currentStep;
                    const isActive = step.id === currentStep;
                    return (
                        <div key={step.id} style={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                            <div style={{ 
                                width: '30px', height: '30px', borderRadius: '50%', 
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                backgroundColor: isCompleted || isActive ? '#0066ff' : '#fff',
                                border: isCompleted || isActive ? 'none' : '2px solid #e5e7eb',
                                color: isCompleted || isActive ? '#fff' : '#6b7280',
                                fontWeight: '600', fontSize: '0.8rem', transition: 'all 0.3s ease'
                            }}>
                                {isCompleted ? <Check size={16} /> : step.id}
                            </div>
                            <span style={{ marginTop: '8px', fontSize: '0.75rem', fontWeight: isActive ? '600' : '500', color: isActive ? '#0066ff' : '#6b7280' }} className={styles.hideOnMobile}>
                                {step.label}
                            </span>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

// --- Main Wizard Component ---
const JobCreationWizard = () => {

    const navigate = useNavigate();
    const location = useLocation();
    const [step, setStep] = useState(1);
    const [isLoading, setIsLoading] = useState(false);
    const [username, setUsername] = useState('');
    
    // Initial State
    const [jobData, setJobData] = useState({
        searchTerm: '',
        professions: [], 
        title: '',
        description: '',
        postcode: '',
        suburb: '',
        state: '',
        address: '', // Add address field
        locationDisplay: '', 
        timeline: '',
        budgetRange: '',
        customBudget: '',
        images: [],
        projectId: null,
        publishMode: 'broadcast',
        taskId: null,
        projectAddress: null // Store project address if available
    });

    // --- USER AUTH CHECK ---
    useEffect(() => {
        const token = localStorage.getItem('token');
        if (token) {
            try {
                const decoded = jwtDecode(token);
                setUsername(decoded.UserName || decoded.unique_name || 'User');
            } catch (e) {
                console.error("Token decode error", e);
            }
        }
    }, []);

    // --- INITIALIZATION EFFECT ---
    useEffect(() => {
        if (location.state) {
            const { projectId, taskId, taskName, taskDescription, professionId, professionName, postcode, suburb, projectState, mode, projectAddress } = location.state;
            
            setJobData(prev => {
                const updatedData = {
                    ...prev,
                    projectId: projectId ? parseInt(projectId) : null,
                    taskId: taskId ? parseInt(taskId) : null,
                    title: taskName || prev.title,
                    description: taskDescription || prev.description,
                    postcode: postcode || prev.postcode,
                    suburb: suburb || prev.suburb,
                    state: projectState || prev.state,
                    address: projectAddress || '', // Pre-fill address if from project
                    projectAddress: projectAddress || null, // Keep reference
                    publishMode: mode || 'broadcast',
                };
                if (suburb && projectState && postcode) {
                    updatedData.locationDisplay = projectAddress || `${suburb}, ${projectState} ${postcode}`;
                }
                if (professionId && professionName) {
                    updatedData.professions = [{ professionId: parseInt(professionId), name: professionName }];
                }
                if (!professionId && taskName) {
                    updatedData.searchTerm = taskName;
                }
                return updatedData;
            });

            if (professionId && professionName) setStep(2); 
            else if (taskName) setStep(1); 
        }
    }, [location.state]);

    // Safety refs for location persistence
    const postcodeRef = useRef(jobData.postcode || '');
    const suburbRef = useRef(jobData.suburb || '');

    useEffect(() => {
        if (typeof jobData.postcode === 'string' && jobData.postcode.trim()) postcodeRef.current = jobData.postcode;
    }, [jobData.postcode]);

    useEffect(() => {
        if (typeof jobData.suburb === 'string' && jobData.suburb.trim()) suburbRef.current = jobData.suburb;
    }, [jobData.suburb]);

    const updateJobData = (field, value) => {
        setJobData(prev => ({ ...prev, [field]: value }));
    };

    const handleCancel = () => {
        const myType = localStorage.getItem('userType');
        if (window.confirm("Are you sure you want to cancel? Any unsaved progress will be lost.")) {
            if (myType === "Home") navigate('/newhome'); 
            else navigate('/businessdash'); 
        }
    };

    const handleSubmit = async () => {
        if (jobData.professions.length === 0) { showToast('error', 'Please select a profession.'); setStep(1); return; }
        if (!jobData.title.trim()) { showToast('error', 'Please enter a job title.'); setStep(2); return; }
        
        const effectivePostcode = (jobData.postcode ?? '').toString().trim() || (postcodeRef.current ?? '').toString().trim();
        const effectiveSuburb = (jobData.suburb ?? '').toString().trim() || (suburbRef.current ?? '').toString().trim();

        if (!effectivePostcode) { showToast('error', 'Please enter a postcode.'); setStep(2); return; }

        setIsLoading(true);
        try {
            const myType = localStorage.getItem('userType');
            const homeUserId = localStorage.getItem('loggedInHomeUserId');
            const businessUserId = localStorage.getItem('loggedInBusinessUserId');
            let myUserId = 0;
            let myUserType = "HomeUser";

            if (myType === "Home") myUserId = homeUserId;
            else { myUserId = businessUserId; myUserType = "BusinessUser"; }

            if (!myUserId) throw new Error("Could not identify user. Please log in again.");

            const payload = {
                userId: parseInt(myUserId, 10),
                name: jobData.title,
                description: jobData.description,
                profession: jobData.professions.map(p => ({ ProfessionId: p.professionId, name: p.name })),
                postcode: effectivePostcode,
                mystate: jobData.state,
                suburb: effectiveSuburb,
                address: jobData.address, // Add address to payload
                budget: jobData.budgetRange,
                budgetAmount: jobData.customBudget ? parseFloat(jobData.customBudget) : null,
                deadline: jobData.timeline,
                siteVisit: "By appointment",
                isPublish: jobData.publishMode === 'broadcast',
                isNew: true,
                userType: myUserType,
                projectId: jobData.projectId || null, 
                taskId: jobData.taskId || null 
            };

            const response = await api.post('/api/Jobs/createjob', payload);
            const newJobId = response.data.job_id;

            if (newJobId && jobData.images.length > 0) {
                const formData = new FormData();
                jobData.images.forEach(img => formData.append('files', img.file));
                await api.post(`/api/Jobs/upload/${newJobId}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
            }

            const isPublish = jobData.publishMode === 'broadcast';
            showToast('success', isPublish ? 'Job Posted Publicly!' : 'Job Saved.');
            
            if (!isPublish) navigate(`/job-console/${newJobId}`, { state: { openInviteModal: true } });
            else navigate(`/job-console/${newJobId}`);

        } catch (error) {
            console.error(error);
            showToast('error', `Failed to post job: ${error.response?.data?.message || error.message}`);
        } finally {
            setIsLoading(false);
        }
    };

    const renderStep = () => {
        switch (step) {
            case 1:
                return <Step1_ServiceSelection onUpdate={updateJobData} onNext={() => setStep(2)} jobData={jobData} onCancel={handleCancel} />;
            case 2:
                return <Step2_JobDetails onUpdate={updateJobData} onNext={() => setStep(3)} onBack={() => setStep(1)} jobData={jobData} onCancel={handleCancel} />;
            case 3:
                return <Step3_Logistics onUpdate={updateJobData} onNext={() => setStep(4)} onBack={() => setStep(2)} jobData={jobData} onCancel={handleCancel} />;
            case 4:
                return <Step4_ImagesAndSubmit onUpdate={updateJobData} onBack={() => setStep(3)} onSubmit={handleSubmit} jobData={jobData} onCancel={handleCancel} />;
            default:
                return <Step1_ServiceSelection onUpdate={updateJobData} onNext={() => setStep(2)} jobData={jobData} onCancel={handleCancel} />;
        }
    };

    return (
        <div style={{minHeight: '100vh', backgroundColor: '#f9fafb', display: 'flex', flexDirection: 'column', paddingTop: '60px'}}>
            <ModernNavbarPureCSS UserName={username} />
            <div style={{
                background: 'linear-gradient(to bottom, #d4af37 0%, #f9e68c 30%, #c49c25 60%, #8a6e14 100%)',
                padding: '1.5rem 2rem',
                borderTop: '1px solid #fffae0',
                borderBottom: '1px solid #49310a',
                color: 'white',
                marginBottom: '2rem'
            }}>
                <div style={{ maxWidth: '1200px', margin: '0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                        <h1 style={{ margin: 0, fontSize: '1.5rem', fontWeight: '600', color: '#011746' }}>Post a New Job</h1>
                        <p style={{ margin: '0.25rem 0 0 0', color: '#011746', opacity: 0.9 }}>Connect with top-rated professionals for your project</p>
                    </div>
                    <button onClick={handleCancel} style={{
                        background: 'rgba(255, 255, 255, 0.2)', border: '1px solid rgba(255, 255, 255, 0.4)', color: '#011746',
                        padding: '0.5rem 1rem', borderRadius: '0.375rem', cursor: 'pointer', fontSize: '0.9rem', fontWeight: '600', transition: 'all 0.2s'
                    }}>Cancel</button>
                </div>
            </div>
            <div style={{ flex: 1, paddingBottom: '3rem' }}>
                <WizardProgressBar currentStep={step} />
                <div className={`${styles.jobDetailNew_container} ${styles.jobDetailNew_mainContent}`} style={{ marginTop: '0' }}>
                    {isLoading ? <Loader2 /> : renderStep()}
                </div>
            </div>
        </div>
    );
};

// --- STEP 1: SMART SERVICE SELECTION (UPDATED) ---
const Step1_ServiceSelection = ({ onUpdate, onNext, jobData, onCancel }) => {
    
    // Use existing title or searchTerm to pre-fill input if user navigates back
    const [smartInput, setSmartInput] = useState(jobData.title || jobData.searchTerm || "");
    const [isAiSearching, setIsAiSearching] = useState(false);

    const handleUpdateProfessions = (newProfessions) => {
        onUpdate('professions', newProfessions);
    };

    const handleRemoveProfession = (id) => {
        const updated = jobData.professions.filter(p => p.professionId !== id);
        onUpdate('professions', updated);
    };

    // --- LOGIC FIX: Explicit Search Action ---
    const executeSmartSearch = async () => {
        if(smartInput.trim().length < 3) {
            showToast("error", "Please enter at least 3 characters");
            return;
        }

        setIsAiSearching(true);
        try {
            const res = await api.get(`/api/Jobs/AISmartSearch?search=${encodeURIComponent(smartInput)}`);
            const matches = res.data || [];

            if (matches.length > 0) {
                const bestMatch = matches[0];
                // Select the profession but DO NOT advance. Let user review.
                onUpdate('professions', [{ professionId: bestMatch.id, name: bestMatch.name }]);
                showToast('success', `Found match: ${bestMatch.name}`);
            } else {
                showToast('info', 'No direct match found. Please select from the list below.');
            }
        } catch (err) {
            console.error("AI Search Error", err);
            showToast('error', 'Search failed. Please select manually.');
        } finally {
            setIsAiSearching(false);
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            executeSmartSearch();
        }
    };

    // Disabled until profession is selected
    const isNextDisabled = !jobData.professions || jobData.professions.length === 0;

    return (
        <div className={styles.jobDetailNew_card}>
            <div className={styles.jobDetailNew_cardContent}>
                <h2 className={styles.jobDetailNew_cardTitle} style={{justifyContent: 'center', fontSize: '1.5rem', marginBottom: '0.5rem'}}>
                    What needs to be done?
                </h2>
                <p className={styles.jobDetailNew_textGray600} style={{textAlign: 'center', marginBottom: '2rem'}}>
                    Type your request (e.g. "Fix leaking tap") or select a category below.
                </p>
                
                {/* AI / Smart Input Box WITH BUTTON */}
                <div style={{
                    display: 'flex', 
                    gap: '10px', 
                    maxWidth: '700px', 
                    margin: '0 auto 2rem auto',
                    alignItems: 'center'
                }}>
                    <div style={{position: 'relative', flex: 1}}>
                        <div className={styles.jobDetailNew_inputWithIconContainer} style={{boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'}}>
                            <Sparkles className={styles.jobDetailNew_inputIcon} style={{color: '#d4af37'}} />
                            <input 
                                type="text" 
                                className={styles.jobDetailNew_input} 
                                style={{paddingLeft: '45px', borderColor: '#d4af37', height: '50px', fontSize: '1.1rem'}}
                                placeholder="Describe your job..." 
                                value={smartInput}
                                onChange={(e) => setSmartInput(e.target.value)}
                                onKeyDown={handleKeyDown}
                            />
                        </div>
                    </div>
                    <button 
                        onClick={executeSmartSearch}
                        disabled={isAiSearching || smartInput.length < 3}
                        className={styles.jobDetailNew_button}
                        style={{
                            height: '50px', 
                            padding: '0 25px', 
                            backgroundColor: '#0066ff', 
                            color: 'white',
                            fontWeight: '600',
                            borderRadius: '0.375rem',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                        }}
                    >
                        {isAiSearching ? <Loader2 size={18} className={styles.jobDetailNew_spin}/> : <SearchIcon size={18} />}
                        Find
                    </button>
                </div>

                {/* --- DISPLAY SELECTED PROFESSIONS (High Contrast Pills) --- */}
                {jobData.professions && jobData.professions.length > 0 && (
                    <div style={{
                        maxWidth: '700px', 
                        margin: '0 auto 2rem auto', 
                        textAlign: 'center',
                        animation: 'fadeIn 0.5s'
                    }}>
                        <h3 style={{
                            color: '#111827', 
                            marginBottom: '1rem', 
                            fontSize: '1.1rem', 
                            fontWeight: '600'
                        }}>
                            The following professionals will be notified:
                        </h3>
                        <div style={{display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: '10px'}}>
                            {jobData.professions.map(p => (
                                <div key={p.professionId} style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    padding: '12px 30px',
                                    backgroundColor: '#fff',
                                    border: '2px solid #000',
                                    borderRadius: '50px',
                                    fontSize: '1.2rem',
                                    fontWeight: '700',
                                    color: '#000',
                                    boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                                }}>
                                    {p.name}
                                    <button 
                                        onClick={() => handleRemoveProfession(p.professionId)}
                                        style={{
                                            marginLeft: '15px', 
                                            background: 'none', 
                                            border: 'none', 
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center'
                                        }}
                                        title="Remove"
                                    >
                                        <X size={20} color="#000" />
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                <div style={{borderTop: '1px solid #e5e7eb', margin: '2rem 0', position: 'relative'}}>
                     <span style={{position: 'absolute', top: '-12px', left: '50%', transform: 'translateX(-50%)', background: '#fff', padding: '0 15px', color: '#9ca3af', fontSize: '0.9rem', fontWeight: '500'}}>
                        OR SELECT MANUALLY
                     </span>
                </div>

                <ProfessionSelector 
                    selectedProfessions={jobData.professions || []}
                    onUpdate={handleUpdateProfessions}
                />

                <div style={{marginTop: '2rem', display: 'flex', justifyContent: 'flex-end', gap: '1rem'}}>
                    <button 
                        onClick={() => onNext()} 
                        disabled={isNextDisabled} 
                        className={`${styles.jobDetailNew_button} ${styles.jobDetailNew_buttonPrimary}`} 
                        style={{
                            padding: '12px 50px', 
                            fontSize: '1.1rem',
                            opacity: isNextDisabled ? 0.5 : 1, 
                            cursor: isNextDisabled ? 'not-allowed' : 'pointer'
                        }}
                    >
                        Next
                    </button>
                </div>
            </div>
        </div>
    );
};

// --- STEP 2: DETAILS ---
const Step2_JobDetails = ({ jobData, onUpdate, onNext, onBack, onCancel }) => {
    
    // Determine initial mode: if job address matches project address, use project mode
    const initialMode = (jobData.projectId && jobData.projectAddress && jobData.address === jobData.projectAddress) 
        ? 'project' 
        : 'specific';
    
    const [addressMode, setAddressMode] = useState(initialMode);

    const handleLocationSelect = (loc) => {
        onUpdate('locationDisplay', loc.fullAddress);
        onUpdate('postcode', loc.postcode);
        onUpdate('suburb', loc.suburb);
        onUpdate('state', loc.state);
        onUpdate('address', loc.fullAddress); // Update address state
    };

    const handleModeChange = (mode) => {
        setAddressMode(mode);
        if (mode === 'project' && jobData.projectAddress) {
            onUpdate('locationDisplay', jobData.projectAddress);
            onUpdate('address', jobData.projectAddress);
            // Optionally reset postcode/suburb if they are not part of project address string
            // For now assuming projectAddress string is sufficient for display
        } else if (mode === 'specific') {
            // Clear or keep previous? Maybe clear to force user to enter
            onUpdate('locationDisplay', '');
            onUpdate('address', '');
            onUpdate('postcode', '');
        }
    };

    const handleNext = () => {
        if (!jobData.title || jobData.title.trim().length < 5) {
             showToast('error', 'Please enter a valid job title (at least 5 characters).');
             return;
        }
        if (!jobData.description || jobData.description.trim().length < 10) {
             showToast('error', 'Please enter a job description (at least 10 characters).');
             return;
        }
        if (!jobData.postcode && !jobData.address) { // Allow address as fallback
             showToast('error', 'Please provide a valid location.');
             return;
        }
        onNext();
    };

    return (
        <div className={styles.jobDetailNew_card}>
            <div className={styles.jobDetailNew_cardHeader}>
                <h3 className={styles.jobDetailNew_cardTitle}><FileText className={`${styles.jobDetailNew_iconMd} ${styles.jobDetailNew_textBlue600}`} />Job Details & Location</h3>
            </div>
            <div className={`${styles.jobDetailNew_cardContent} ${styles.jobDetailNew_cardContentSpaceY6}`}>
                <div>
                    <label htmlFor="title" className={styles.jobDetailNew_label}>Job Title</label>
                    <input
                        id="title"
                        type="text"
                        placeholder="e.g., Replace electric hot water system"
                        value={jobData.title}
                        onChange={e => onUpdate('title', e.target.value)}
                        className={styles.jobDetailNew_input}
                    />
                    <p style={{fontSize: '0.8rem', color: '#6b7280', marginTop: '4px'}}>
                        Describe the task, not the trade (e.g., "Install 3 ceiling fans" instead of "Electrician").
                        &nbsp;|&nbsp; Selected: <strong>{jobData.professions.map(p => p.name).join(', ')}</strong>
                    </p>
                </div>
                <div>
                    <label htmlFor="description" className={styles.jobDetailNew_label}>Description</label>
                    <JobDescriptionEnhancer
                        description={jobData.description}
                        professionName={jobData.professions?.[0]?.name || ''}
                        onDescriptionChange={(val) => onUpdate('description', val)}
                        onTitleChange={(val) => { if (!jobData.title || jobData.title.trim().length === 0) onUpdate('title', val); }}
                        textareaClassName={styles.jobDetailNew_textarea}
                        placeholder={"What needs to be done:\n\nIs it an emergency?:\n\nDo you have the materials, or does the pro need to supply them?:"}
                        rows={5}
                    />
                </div>

                <div>
                    <label className={styles.jobDetailNew_label}>Job Location</label>
                    
                    {jobData.projectId && jobData.projectAddress && (
                        <div style={{display: 'flex', gap: '20px', marginBottom: '15px'}}>
                            <label style={{display: 'flex', alignItems: 'center', cursor: 'pointer'}}>
                                <input 
                                    type="radio" 
                                    name="addressMode" 
                                    value="project" 
                                    checked={addressMode === 'project'} 
                                    onChange={() => handleModeChange('project')}
                                    style={{marginRight: '8px'}}
                                />
                                Use Project Address
                            </label>
                            <label style={{display: 'flex', alignItems: 'center', cursor: 'pointer'}}>
                                <input 
                                    type="radio" 
                                    name="addressMode" 
                                    value="specific" 
                                    checked={addressMode === 'specific'} 
                                    onChange={() => handleModeChange('specific')}
                                    style={{marginRight: '8px'}}
                                />
                                Use Different Address
                            </label>
                        </div>
                    )}

                    {addressMode === 'project' && jobData.projectId && jobData.projectAddress ? (
                        <div style={{padding: '10px', backgroundColor: '#f3f4f6', borderRadius: '6px', color: '#374151'}}>
                            <strong>Project Address:</strong> {jobData.projectAddress}
                        </div>
                    ) : (
                        <LocationInputGroup 
                            label="" 
                            value={jobData.locationDisplay}
                            onLocationSelect={handleLocationSelect}
                            suburb={jobData.suburb}
                            state={jobData.state}
                            error={!jobData.postcode && jobData.locationDisplay ? "Please select a valid address from the list" : null}
                            placeholder="Enter specific job address..."
                        />
                    )}
                </div>
                
                <div style={{display: 'flex', justifyContent: 'space-between', marginTop: '2rem'}}>
                    <button onClick={onBack} className={`${styles.jobDetailNew_button} ${styles.jobDetailNew_buttonOutline}`}>
                        <ArrowLeft size={16} style={{marginRight: '8px'}}/> Back
                    </button>
                    <button onClick={handleNext} className={`${styles.jobDetailNew_button} ${styles.jobDetailNew_buttonPrimary}`}>
                        Next
                    </button>
                </div>
            </div>
        </div>
    );
};

// --- STEP 3: LOGISTICS ---
const Step3_Logistics = ({ jobData, onUpdate, onNext, onBack, onCancel }) => {
    const budgetRanges = ["$500 - $1000", "$1000 - $2500", "$2500 - $5000", "$5000 - $10000", "$10000+"];
    const timelineOptions = ["Urgently", "Within 1 week", "Within 2 weeks", "2 weeks or longer"];

    const handleNext = () => {
        if (!jobData.budgetRange && !jobData.customBudget) {
             showToast('error', 'Please select a budget range or enter an amount.');
             return;
        }
        if (!jobData.timeline) {
             showToast('error', 'Please select when you need the job done.');
             return;
        }
        onNext();
    };

    return (
        <div className={styles.jobDetailNew_card}>
            <div className={styles.jobDetailNew_cardHeader}>
                <h3 className={styles.jobDetailNew_cardTitle}><DollarSign className={`${styles.jobDetailNew_iconMd} ${styles.jobDetailNew_textGreen500}`} />Budget & Timeline</h3>
            </div>
            <div className={`${styles.jobDetailNew_cardContent} ${styles.jobDetailNew_cardContentSpaceY6}`}>
                <div>
                    <label className={styles.jobDetailNew_label}>What is your estimated budget?</label>
                     <div className={`${styles.jobDetailNew_grid} ${styles.jobDetailNew_md_gridCols2}`} style={{gap: '1rem'}}>
                        <select value={jobData.budgetRange} onChange={e => onUpdate('budgetRange', e.target.value)} className={styles.jobDetailNew_selectTrigger}>
                            <option value="">Select a range</option>
                            {budgetRanges.map(range => <option key={range} value={range}>{range}</option>)}
                        </select>
                        <div className={styles.jobDetailNew_inputWithIconContainer}>
                            <DollarSign className={styles.jobDetailNew_inputIcon} />
                            <input type="number" placeholder="Or enter exact amount" value={jobData.customBudget} onChange={e => onUpdate('customBudget', e.target.value)} className={`${styles.jobDetailNew_input} ${styles.jobDetailNew_inputPl10}`} />
                        </div>
                    </div>
                </div>
                 <div>
                    <label className={styles.jobDetailNew_label}>How soon do you need it done?</label>
                    <div className={styles.jobDetailNew_timelineButtonGrid}>
                        {timelineOptions.map(option => (
                             <button key={option} type="button" className={`${styles.jobDetailNew_button} ${styles.jobDetailNew_timelineButton} ${jobData.timeline === option ? styles.jobDetailNew_bgBlue500 : styles.jobDetailNew_buttonOutline}`} onClick={() => onUpdate('timeline', option)}>
                                <span className={styles.jobDetailNew_timelineButtonText}>{option}</span>
                             </button>
                        ))}
                    </div>
                </div>
                <div style={{display: 'flex', justifyContent: 'space-between', marginTop: '2rem'}}>
                    <button onClick={onBack} className={`${styles.jobDetailNew_button} ${styles.jobDetailNew_buttonOutline}`}>
                        <ArrowLeft size={16} style={{marginRight: '8px'}}/> Back
                    </button>
                    <button onClick={handleNext} className={`${styles.jobDetailNew_button} ${styles.jobDetailNew_buttonPrimary}`}>
                        Next
                    </button>
                </div>
            </div>
        </div>
    );
};

// --- STEP 4: IMAGES & SUBMIT ---
const Step4_ImagesAndSubmit = ({ jobData, onUpdate, onBack, onSubmit, onCancel }) => {
    
    const isBroadcast = jobData.publishMode === 'broadcast';
    const submitLabel = isBroadcast ? "Post Job Now" : "Save & Find Pros";
    const SubmitIcon = isBroadcast ? Megaphone : UserPlus;

    const handleFilesChange = (newFiles) => {
        onUpdate('images', newFiles);
    };

    return (
        <div className={styles.jobDetailNew_card}>
            <div className={styles.jobDetailNew_cardHeader}>
                <h3 className={styles.jobDetailNew_cardTitle}>
                    <Camera className={`${styles.jobDetailNew_iconMd} ${styles.jobDetailNew_textAmber600}`} />
                    Job Files & Review
                </h3>
            </div>
            <div className={`${styles.jobDetailNew_cardContent} ${styles.jobDetailNew_cardContentSpaceY6}`}>
                
                <JobFileUploader 
                    currentFiles={jobData.images}
                    onFilesChange={handleFilesChange}
                />
                
                <div style={{backgroundColor: '#f3f4f6', padding: '15px', borderRadius: '8px', fontSize: '0.9rem'}}>
                    <h4 style={{margin: '0 0 10px 0', fontWeight: '600'}}>Summary:</h4>
                    <p><strong>Job:</strong> {jobData.title}</p>
                    <p><strong>Location:</strong> {jobData.address || `${jobData.suburb || ''}${jobData.suburb ? ', ' : ''}${jobData.state || ''} ${jobData.postcode || ''}`.trim()}</p>
                    <p><strong>Budget:</strong> {jobData.customBudget ? `$${jobData.customBudget}` : jobData.budgetRange}</p>
                </div>

                <div style={{display: 'flex', justifyContent: 'space-between', marginTop: '2rem'}}>
                    <button onClick={onBack} className={`${styles.jobDetailNew_button} ${styles.jobDetailNew_buttonOutline}`}>
                        <ArrowLeft size={16} style={{marginRight: '8px'}}/> Back
                    </button>
                    
                    <button 
                        onClick={onSubmit} 
                        className={`${styles.jobDetailNew_button} ${styles.jobDetailNew_buttonPrimary}`}
                        style={!isBroadcast ? { backgroundColor: '#10B981', borderColor: '#10B981', color: 'white' } : {}}
                    >
                        <SubmitIcon size={16} style={{marginRight: '8px'}}/> 
                        {submitLabel}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default JobCreationWizard;